/**
 * OpenAPI ドキュメントの読み解き（FR-W9）。
 *
 * サーバの `/openapi.json` をそのまま画面に出すための前処理。
 *
 * **描画ライブラリは足さない。** Swagger UI などを持ち込むと数百 KB 増えるうえ、
 * この UI の他の画面と見た目が揃わない。必要なのは
 * 「エンドポイントを一覧して、パラメータと本文の形が読める」ことだけなので、
 * その分だけを純関数として用意する。
 */

export type Json = Record<string, unknown>;

export interface OpenApiParameter {
  readonly name: string;
  readonly in: string;
  readonly required: boolean;
  readonly type: string;
  readonly description?: string;
}

export interface OpenApiRequestBody {
  readonly required: boolean;
  readonly contentType: string;
  readonly schema: Json | undefined;
}

export interface OpenApiResponse {
  readonly status: string;
  readonly description: string;
  readonly schema: Json | undefined;
}

export interface OpenApiOperation {
  readonly group: string;
  readonly method: string;
  readonly path: string;
  readonly summary: string;
  readonly description?: string;
  readonly parameters: readonly OpenApiParameter[];
  readonly requestBody?: OpenApiRequestBody;
  readonly responses: readonly OpenApiResponse[];
}

const METHODS = ["get", "post", "put", "patch", "delete"] as const;

const asObject = (value: unknown): Json | undefined =>
  typeof value === "object" && value !== null && !Array.isArray(value) ? (value as Json) : undefined;

const asString = (value: unknown): string | undefined =>
  typeof value === "string" ? value : undefined;

/**
 * パスからグループ名を決める。
 *
 * OpenAPI の `tags` を書いていないので、`/v1/runs/{runId}/events` → `runs` の
 * ようにパスから導く。分類のためだけなので、形が想定外でも落とさない。
 */
export function groupOf(path: string): string {
  const segments = path.split("/").filter((segment) => segment !== "");
  if (segments.length === 0) return "other";
  // 先頭が API バージョンならその次を使う。
  const candidate = /^v\d+$/.test(segments[0] ?? "") ? segments[1] : segments[0];
  return candidate ?? "other";
}

/** `$ref` を辿る。辿れなければ `null`（例外にはしない）。 */
export function resolveRef(doc: Json, schema: unknown): Json | null {
  const object = asObject(schema);
  if (object === undefined) return null;

  const ref = asString(object["$ref"]);
  if (ref === undefined) return object;
  // ローカル参照だけを扱う（外部ファイルは使っていない）。
  if (!ref.startsWith("#/")) return null;

  let current: unknown = doc;
  for (const key of ref.slice(2).split("/")) {
    const next = asObject(current);
    if (next === undefined) return null;
    current = next[key];
  }
  return asObject(current) ?? null;
}

/** `$ref` の指す名前（`#/components/schemas/Scope` → `Scope`）。 */
function refName(schema: unknown): string | undefined {
  const ref = asString(asObject(schema)?.["$ref"]);
  return ref === undefined ? undefined : ref.split("/").at(-1);
}

/**
 * `{ properties: { source: { const: "git" } } }` のような判別子を
 * `source="git"` の形で取り出す。無ければ null。
 */
function discriminatorOf(schema: unknown): string | null {
  const properties = asObject(asObject(schema)?.["properties"]);
  if (properties === undefined) return null;

  for (const [name, raw] of Object.entries(properties)) {
    const value = asObject(raw)?.["const"];
    if (value !== undefined) return `${name}=${JSON.stringify(value)}`;
  }
  return null;
}

/** 型を人が読める 1 行にする。 */
function typeLabel(doc: Json, schema: unknown): string {
  const object = asObject(schema);
  if (object === undefined) return "unknown";

  const named = refName(object);
  if (named !== undefined) return named;

  if (object["const"] !== undefined) return JSON.stringify(object["const"]);
  if (Array.isArray(object["enum"])) return object["enum"].map((v) => JSON.stringify(v)).join(" | ");
  if (Array.isArray(object["oneOf"])) {
    // `object | object | object` は何も伝えない。判別子（const を持つ property）が
    // あればそれを見せ、無ければ重複を潰す。
    const labels = object["oneOf"].map((v) => discriminatorOf(v) ?? typeLabel(doc, v));
    return [...new Set(labels)].join(" | ");
  }

  const type = object["type"];
  if (Array.isArray(type)) return type.join(" | ");
  if (type === "array") {
    const items = object["items"];
    return `${typeLabel(doc, items)}[]`;
  }
  return asString(type) ?? "unknown";
}

/** ドキュメント全体を「メソッド × パス」の一覧に潰す。 */
export function flattenOperations(doc: Json): OpenApiOperation[] {
  const paths = asObject(doc["paths"]) ?? {};
  const operations: OpenApiOperation[] = [];

  for (const [path, rawItem] of Object.entries(paths)) {
    const item = asObject(rawItem);
    if (item === undefined) continue;

    for (const method of METHODS) {
      const operation = asObject(item[method]);
      if (operation === undefined) continue;

      const parameters = (Array.isArray(operation["parameters"]) ? operation["parameters"] : [])
        .map((raw) => asObject(raw))
        .filter((raw): raw is Json => raw !== undefined)
        .map((raw) => ({
          name: asString(raw["name"]) ?? "",
          in: asString(raw["in"]) ?? "query",
          required: raw["required"] === true,
          type: typeLabel(doc, raw["schema"]),
          ...(asString(raw["description"]) === undefined
            ? {}
            : { description: asString(raw["description"])! }),
        }));

      operations.push({
        group: groupOf(path),
        method,
        path,
        summary: asString(operation["summary"]) ?? "",
        ...(asString(operation["description"]) === undefined
          ? {}
          : { description: asString(operation["description"])! }),
        parameters,
        ...(requestBodyOf(operation) === undefined
          ? {}
          : { requestBody: requestBodyOf(operation)! }),
        responses: responsesOf(operation),
      });
    }
  }

  // グループ → パス → メソッド定義順。表示のたびに並びが変わらないようにする。
  return operations.sort(
    (a, b) =>
      a.group.localeCompare(b.group) ||
      a.path.localeCompare(b.path) ||
      METHODS.indexOf(a.method as (typeof METHODS)[number]) -
        METHODS.indexOf(b.method as (typeof METHODS)[number]),
  );
}

function firstContent(container: Json | undefined): { contentType: string; schema?: Json } | undefined {
  const content = asObject(container?.["content"]);
  if (content === undefined) return undefined;
  const [contentType, body] = Object.entries(content)[0] ?? [];
  if (contentType === undefined) return undefined;
  const schema = asObject(asObject(body)?.["schema"]);
  return { contentType, ...(schema === undefined ? {} : { schema }) };
}

function requestBodyOf(operation: Json): OpenApiRequestBody | undefined {
  const body = asObject(operation["requestBody"]);
  if (body === undefined) return undefined;
  const content = firstContent(body);
  return {
    required: body["required"] === true,
    contentType: content?.contentType ?? "application/json",
    schema: content?.schema,
  };
}

function responsesOf(operation: Json): OpenApiResponse[] {
  const responses = asObject(operation["responses"]) ?? {};
  return Object.entries(responses)
    .map(([status, raw]) => {
      const object = asObject(raw);
      const content = firstContent(object);
      return {
        status,
        description: asString(object?.["description"]) ?? "",
        schema: content?.schema,
      };
    })
    .sort((a, b) => a.status.localeCompare(b.status));
}

export interface SchemaField {
  readonly name: string;
  readonly type: string;
  readonly required: boolean;
  readonly description?: string;
}

/**
 * オブジェクトスキーマをフィールドの表にする。
 *
 * 入れ子は展開しない（`$ref` は型名だけ出す）。全部展開すると
 * 画面が読めなくなるうえ、循環参照で無限に潜る。
 */
export function describeSchema(doc: Json, schema: unknown): SchemaField[] {
  const resolved = resolveRef(doc, schema);
  if (resolved === null) return [];

  const properties = asObject(resolved["properties"]);
  if (properties === undefined) return [];

  const required = new Set(
    (Array.isArray(resolved["required"]) ? resolved["required"] : []).filter(
      (value): value is string => typeof value === "string",
    ),
  );

  return Object.entries(properties).map(([name, raw]) => ({
    name,
    type: typeLabel(doc, raw),
    required: required.has(name),
    ...(asString(asObject(raw)?.["description"]) === undefined
      ? {}
      : { description: asString(asObject(raw)?.["description"])! }),
  }));
}
